import React, { useState, useEffect, useCallback } from "react";
import { JourneyStepInput, ContentSelectionType } from "../../../types/journey";
import {
  Category,
  JourneyContentRow,
  CustomizableGameConfig,
} from "../../../types/games";

import { GameName } from "../../../types/games";
import { useTeamContext } from "../../../contexts/TeamContext";
import { useApi } from "../../../hooks/useApi";

// Import game type constants and customization configs
import {
  THIS_OR_THAT,
  TRIVIA,
  PICTURE_PUZZLE,
  WORD_LINK,
  WORDSMITH,
  TYPING_TEST,
} from "../../../constants";

// Import content conversion utilities
import {
  contentItemsToRows,
  rowsToContentItems,
} from "../../../utils/contentFormatters";

// Import content creation forms
import { JourneyThisOrThatCreateForm } from "./custom/CustomThisOrThat";
import { JourneyTriviaCreateForm } from "./custom/CustomTrivia";
import { JourneyPicturePuzzleCreateForm } from "./custom/CustomPicturePuzzle";

import { GameConfigSetter } from "./GameConfigSetter";

// Game type definition
interface GameType {
  id: string;
  name: string;
  title?: string;
  emoji?: string;
  description?: string;
}

interface TouchpointStepProps {
  touchpointIndex: number;
  totalTouchpoints: number;
  touchpoint: JourneyStepInput;
  onChange: (touchpoint: JourneyStepInput) => void;
  gameTypes: GameType[];
  categories: Category[]; // This will be used as fallback
  registerStateCollector: (collector: () => JourneyStepInput) => void;
  // New props for parent-managed content rows
  contentRows: JourneyContentRow[];
  onContentRowsChange: (
    touchpointId: string,
    newRows: JourneyContentRow[],
  ) => void;
}

/**
 * TouchpointConfigStep component
 *
 * A dedicated step in the journey creation wizard for configuring a single touchpoint (setting game type, day offset and time, and customization of the game)
 */
const TouchpointConfigStep: React.FC<TouchpointStepProps> = ({
  touchpointIndex,
  totalTouchpoints,
  touchpoint,
  onChange,
  gameTypes,
  categories,
  registerStateCollector,
  // Use the content rows and handler from parent
  contentRows,
  onContentRowsChange,
}) => {
  // Get teamId from context to pass to picture puzzle form
  const { teamId } = useTeamContext();

  const api = useApi();

  // Helper function to format time as HH:mm
  const formatTimeValue = (hours: number, minutes: number): string => {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  // Keep state completely local to prevent focus loss
  const [localTouchpoint, setLocalTouchpoint] = useState<JourneyStepInput>(
    () => ({
      ...touchpoint,
    }),
  );

  // Initialize game config from touchpoint if available
  useEffect(() => {
    if (localTouchpoint.game_type_id && !localTouchpoint.game_config) {
      // If we have a game type but no config, set default config
      const selectedGameType = gameTypes.find(
        (g) => g.id === localTouchpoint.game_type_id,
      );
      if (selectedGameType) {
        const gameName = selectedGameType.name as GameName;
        // Default to not allowing rematch - users must opt in to this functionality
        let defaultConfig: CustomizableGameConfig = { allow_rematch: false };

        switch (gameName) {
          // Trivia game focuses on question count and timing
          case TRIVIA:
            defaultConfig = {
              allow_rematch: false,
              questions_per_game: 5,
              time_per_question: 30,
            };
            break;
          // Picture Puzzle only needs max guesses config
          case PICTURE_PUZZLE:
            defaultConfig = {
              allow_rematch: false,
              max_guesses: 5,
            };
            break;
          // Typing Test only needs timeout config
          case TYPING_TEST:
            defaultConfig = {
              allow_rematch: false,
              timeout_seconds: 60,
            };
            break;
          // Word Link requires max turns, strikes, and min players
          case WORD_LINK:
            defaultConfig = {
              allow_rematch: false,
              max_turns: 5,
              min_players: 2, // Required field
              max_strikes: 3,
            };
            break;
          // Wordsmith requires min players per team and team count
          case WORDSMITH:
            defaultConfig = {
              allow_rematch: false,
              game_duration: 60, // Seconds
              min_players_per_team: 1,
              num_teams: 2,
            };
            break;
        }

        // Update local state and parent state
        setLocalTouchpoint((prev) => ({
          ...prev,
          game_config: defaultConfig,
        }));

        onChange({
          ...localTouchpoint,
          game_config: defaultConfig,
        });
      }
    }
  }, [localTouchpoint.game_type_id, gameTypes]);

  // Local state for game-specific categories
  const [gameCategories, setGameCategories] = useState<Category[]>([]);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);

  // Fetch categories for a specific game type
  const fetchCategoriesForGameType = useCallback(
    async (gameTypeId: string) => {
      if (!gameTypeId || !teamId) return;

      // Get the game type name instead of using the ID
      const gameType = gameTypes.find((g) => g.id === gameTypeId);
      if (!gameType || !gameType.name) {
        console.error("Could not find game type name for ID:", gameTypeId);
        setGameCategories(categories);
        return;
      }

      try {
        setIsFetchingCategories(true);
        const response = await api.get(`/v2/games/categories`, {
          params: {
            team: teamId,
            game_type: gameType.name, // Use name instead of ID
          },
        });
        setGameCategories(response.data);
      } catch (error) {
        console.error("Failed to fetch categories for game type:", error);
        // Fall back to using parent-provided categories
        setGameCategories(categories);
      } finally {
        setIsFetchingCategories(false);
      }
    },
    [api, teamId, categories, gameTypes],
  );

  // Determine if custom content is supported for the selected game type
  const getSelectedGameName = useCallback((): GameName | null => {
    const gameType = gameTypes.find(
      (g) => g.id === localTouchpoint.game_type_id,
    );
    // Cast to GameName since we know the game types in our system match the GameName type
    return gameType ? (gameType.name as GameName) : null;
  }, [gameTypes, localTouchpoint.game_type_id]);

  // Initialize parent's content rows if needed when component mounts
  useEffect(() => {
    const touchpointId = touchpointIndex.toString();
    const gameName = getSelectedGameName();

    // Check if we need to initialize content rows in parent
    const hasCustomContent =
      Array.isArray(touchpoint.custom_content) &&
      touchpoint.custom_content.length > 0;

    if (
      hasCustomContent &&
      gameName &&
      touchpoint.content_selection_type === "custom" &&
      // Only if we don't already have rows for this touchpoint in parent state
      !contentRows.some((row) => row.touchpoint_id === touchpointId)
    ) {
      // Safely extract custom content for type safety
      const customContent = touchpoint.custom_content || [];

      // Convert content items to rows and update parent state
      // Custom content is guaranteed to exist and have items by our check above
      const initialRows = contentItemsToRows(customContent);
      onContentRowsChange(touchpointId, initialRows);
    }
    // We only want this effect to run once on mount and when these dependencies change
    // This initialization logic should only run when necessary components change
  }, [
    touchpointIndex,
    getSelectedGameName,
    touchpoint.custom_content,
    touchpoint.content_selection_type,
    contentRows,
    onContentRowsChange,
  ]);

  // Sync from parent whenever touchpoint changes - simplified approach
  useEffect(() => {
    // Simply keep our local UI state in sync with the parent touchpoint data
    setLocalTouchpoint(touchpoint);

    // If content selection type is category and we have a game type, fetch categories
    if (
      touchpoint.content_selection_type === "category" &&
      touchpoint.game_type_id
    ) {
      fetchCategoriesForGameType(touchpoint.game_type_id);
    } else {
      // Initialize gameCategories with the parent-provided categories as fallback
      setGameCategories(categories);
    }

    // Note: We no longer need to manage content rows sync here
    // as we're using the parent-managed contentRows directly
  }, [touchpoint, touchpointIndex, fetchCategoriesForGameType, categories]);

  const isCustomContentSupported = (): boolean => {
    const gameName = getSelectedGameName();
    if (!gameName) return false;
    // Compare as strings to avoid TypeScript issues
    return [THIS_OR_THAT, TRIVIA, PICTURE_PUZZLE].includes(gameName);
  };
  // Handle changes to any touchpoint property - only update local state
  const handleChange = (property: keyof JourneyStepInput, value: any) => {
    setLocalTouchpoint((prev) => {
      const updated = {
        ...prev,
        [property]: value,
      };

      // Set the proper game config when changing game types
      if (property === "game_type_id") {
        // Get the game type name
        const selectedGameType = gameTypes.find((g) => g.id === value);
        if (selectedGameType) {
          // Set default game config based on game type
          const gameName = selectedGameType.name as GameName;
          switch (gameName) {
            case TRIVIA:
              localTouchpoint.game_config = {
                questions_per_game: 5,
                time_per_question: 30,
                allow_rematch: false,
              };
              break;
            case THIS_OR_THAT:
              localTouchpoint.game_config = {
                allow_rematch: false,
              };
              break;
            case PICTURE_PUZZLE:
              localTouchpoint.game_config = {
                max_guesses: 3,
                allow_rematch: false,
              };
              break;
            case TYPING_TEST:
              localTouchpoint.game_config = {
                timeout_seconds: 60,
                allow_rematch: false,
              };
              break;
            case WORD_LINK:
              localTouchpoint.game_config = {
                max_turns: 5,
                min_players: 2,
                max_strikes: 3,
                allow_rematch: false,
              };
              break;
            case WORDSMITH:
              localTouchpoint.game_config = {
                game_duration: 60,
                min_players_per_team: 1,
                num_teams: 2,
                allow_rematch: false,
              };
              break;
            default:
              localTouchpoint.game_config = {
                allow_rematch: false,
              };
          }
        }
      }

      // If updating the game type, and content_selection_type is 'category', fetch appropriate categories
      if (
        property === "game_type_id" &&
        updated.content_selection_type === "category" &&
        value
      ) {
        fetchCategoriesForGameType(value);
      }

      return updated;
    });
  };

  // Register a state collector function that will be called before navigation
  useEffect(() => {
    // Register a collector function that returns the current local state
    registerStateCollector(() => {
      // Make sure we include any custom content that's been created
      // AND preserve all other fields (including custom_message)
      const gameName = getSelectedGameName();

      // Always start with the complete localTouchpoint state
      const collectedState = { ...localTouchpoint };

      // If we have custom content, process it
      if (gameName && localTouchpoint.content_selection_type === "custom") {
        const contentItems = rowsToContentItems(contentRows, gameName);
        collectedState.custom_content = contentItems;
      }

      return collectedState;
    });
  }, [
    registerStateCollector,
    localTouchpoint,
    contentRows,
    getSelectedGameName,
  ]);

  // Handle content selection type change - this is an explicit user action, so update parent
  const handleContentSelectionChange = (type: ContentSelectionType) => {
    const updatedTouchpoint = {
      ...localTouchpoint,
      content_selection_type: type,
    };

    // Reset category or custom content based on selection
    if (type === "surprise_me") {
      delete updatedTouchpoint.category_id;
      delete updatedTouchpoint.custom_content;
      // Clear the contentRows in parent state when switching away from custom
      onContentRowsChange(touchpointIndex.toString(), []);
    } else if (type === "category") {
      delete updatedTouchpoint.custom_content;
      // Clear the contentRows in parent state when switching away from custom
      onContentRowsChange(touchpointIndex.toString(), []);
      // Fetch categories for the selected game type
      if (updatedTouchpoint.game_type_id) {
        fetchCategoriesForGameType(updatedTouchpoint.game_type_id);
      }
      // Set default category if none is selected
      if (!updatedTouchpoint.category_id && gameCategories.length > 0) {
        updatedTouchpoint.category_id = gameCategories[0].id;
      }
    } else if (type === "custom") {
      delete updatedTouchpoint.category_id;
      // We don't need to initialize custom_content immediately
      // It will be created when the user actually adds content
      // This prevents creating empty arrays that display preview boxes
      if (
        updatedTouchpoint.custom_content &&
        updatedTouchpoint.custom_content.length === 0
      ) {
        delete updatedTouchpoint.custom_content;
      }
    }

    // Update local state
    setLocalTouchpoint(updatedTouchpoint);

    // This is a direct user action, safe to update parent
    onChange(updatedTouchpoint);
  };

  // Handle custom content form submission
  const handleCustomContentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all content rows
    const gameName = getSelectedGameName();

    if (!gameName) return;

    // Convert to ContentItemType[] using our utility
    const contentItems = rowsToContentItems(contentRows, gameName);

    if (contentItems.length === 0) {
      console.debug("No valid rows found");
      return;
    }

    // Update the touchpoint with custom content
    const updatedTouchpoint = {
      ...localTouchpoint,
      custom_content: contentItems,
    };

    // Update local state
    setLocalTouchpoint(updatedTouchpoint);

    // Update parent state - this persists the changes
    onChange(updatedTouchpoint);
  };

  // Adapter for the content rows changes that forwards to parent handler
  // This ensures all state is managed by the parent component for better consistency
  const handleContentRowsUpdate = (
    rows: JourneyContentRow[] | React.SetStateAction<JourneyContentRow[]>,
  ) => {
    console.log(
      `[TouchpointConfigStep] handleContentRowsUpdate for touchpoint ${touchpointIndex}`,
      {
        rowsType: typeof rows,
        rowsLength: Array.isArray(rows) ? rows.length : "unknown",
      },
    );

    // Determine the new rows value based on input type
    const newRows =
      typeof rows === "function"
        ? (rows as (prev: JourneyContentRow[]) => JourneyContentRow[])(
            contentRows,
          )
        : rows;

    // Send to parent handler to update the source of truth
    onContentRowsChange(touchpointIndex.toString(), newRows);

    // Also update the local touchpoint for UI state consistency
    const gameName = getSelectedGameName();
    if (gameName && localTouchpoint.content_selection_type === "custom") {
      // Convert to ContentItemType[] using our utility
      const contentItems = rowsToContentItems(newRows, gameName);

      // Update local touchpoint to keep UI in sync with parent state
      setLocalTouchpoint((prev) => ({
        ...prev,
        custom_content: contentItems,
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Configure Touchpoint {touchpointIndex + 1} of {totalTouchpoints}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Customize when this touchpoint will be sent and what content it will
          include.
        </p>
      </div>

      <div className="space-y-4">
        {/* 1. Game type selection - First and most important */}
        <div>
          <label
            htmlFor="game_type_id"
            className="block text-sm font-medium text-gray-700"
          >
            Game Type
          </label>
          <select
            id="game_type_id"
            value={localTouchpoint.game_type_id}
            onChange={(e) => {
              // Update local state
              handleChange("game_type_id", e.target.value);
              // For select dropdowns, update parent immediately
              onChange({
                ...localTouchpoint,
                game_type_id: e.target.value,
              });
            }}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm rounded-md"
          >
            <option value="" disabled>
              Select a game...
            </option>
            {gameTypes.map((game) => (
              <option key={game.id} value={game.id}>
                {game.emoji && `${game.emoji} `}
                {game.title || game.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="custom_message"
            className="block text-sm font-medium text-gray-700"
          >
            Custom Message (Optional)
          </label>
          <div className="mt-1">
            <textarea
              id="custom_message"
              rows={3}
              value={localTouchpoint.custom_message || ""}
              onChange={(e) => handleChange("custom_message", e.target.value)}
              // No onBlur handler needed with navigation-based synchronization
              placeholder="Add a personal message to accompany this touchpoint"
              className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            This message will be included when the touchpoint is sent.
          </p>
        </div>

        {/* Configuration Section - Combines scheduling, content selection, and options */}
        <div className="space-y-6 bg-gray-50 p-4 rounded-md">
          <h4 className="font-medium text-gray-900">Configuration</h4>

          {/* When to send this touchpoint */}
          <div className="space-y-2">
            <h5 className="text-sm font-medium text-gray-700">Scheduling</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="offset_days"
                  className="block text-xs text-gray-600"
                >
                  Day in sequence
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Which day of the journey to send this touchpoint
                </p>
                <input
                  type="number"
                  id="offset_days"
                  min={0}
                  value={localTouchpoint.offset_days}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    handleChange("offset_days", value);
                  }}
                  onBlur={() => {
                    onChange({
                      ...localTouchpoint,
                    });
                  }}
                  // No onBlur handler needed with navigation-based synchronization
                  className="mt-1 focus:ring-brand focus:border-brand block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor="offset_time"
                  className="block text-xs text-gray-600"
                >
                  Time of day
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  What time to send this touchpoint
                </p>
                <input
                  type="time"
                  id="offset_time"
                  step="60"
                  value={formatTimeValue(
                    localTouchpoint.offset_hours || 0,
                    localTouchpoint.offset_minutes || 0,
                  )}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value
                      .split(":")
                      .map(Number);
                    handleChange("offset_hours", hours);
                    handleChange("offset_minutes", minutes);
                  }}
                  onBlur={() => {
                    onChange({
                      ...localTouchpoint,
                    });
                  }}
                  // No onBlur handler needed with navigation-based synchronization
                  className="mt-1 focus:ring-brand focus:border-brand block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Content selection and Other options - Side by side */}
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Content selection - Left column */}
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-700">
                  Content Selection
                </h5>
                <div className="mt-2 space-y-2">
                  {/* "Surprise Me" option */}
                  <div className="flex items-center">
                    <input
                      id="surprise_me"
                      name="content_selection_type"
                      type="radio"
                      checked={
                        localTouchpoint.content_selection_type === "surprise_me"
                      }
                      onChange={() =>
                        handleContentSelectionChange("surprise_me")
                      }
                      className="focus:ring-brand h-4 w-4 text-brand border-gray-300"
                    />
                    <label
                      htmlFor="surprise_me"
                      className="ml-3 block text-sm text-gray-700"
                    >
                      Surprise Me (random content)
                    </label>
                  </div>

                  {/* "Select from Categories" option */}
                  <div className="flex items-center">
                    <input
                      id="select_category"
                      name="content_selection_type"
                      type="radio"
                      checked={
                        localTouchpoint.content_selection_type === "category"
                      }
                      onChange={() => handleContentSelectionChange("category")}
                      className="focus:ring-brand h-4 w-4 text-brand border-gray-300"
                    />
                    <label
                      htmlFor="select_category"
                      className="ml-3 block text-sm text-gray-700"
                    >
                      Select from Categories
                    </label>
                  </div>

                  {/* Category selector - Only shows when category is selected */}
                  {localTouchpoint.content_selection_type === "category" && (
                    <div className="ml-7 mt-2">
                      {isFetchingCategories ? (
                        <div className="text-sm text-gray-500">
                          Loading categories...
                        </div>
                      ) : (
                        <select
                          id="category_id"
                          value={localTouchpoint.category_id || ""}
                          onChange={(e) => {
                            // Update local state
                            handleChange("category_id", e.target.value);
                            // Update parent immediately for select dropdown
                            onChange({
                              ...localTouchpoint,
                              category_id: e.target.value,
                            });
                          }}
                          className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-brand focus:border-brand rounded-md"
                        >
                          <option value="" disabled>
                            {gameCategories.length === 0
                              ? "No categories available for this game type"
                              : "Select a category..."}
                          </option>
                          {gameCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* "Create Custom Content" option - only show for supported game types */}
                  {isCustomContentSupported() && (
                    <div className="flex items-center">
                      <input
                        id="custom_content"
                        name="content_selection_type"
                        type="radio"
                        checked={
                          localTouchpoint.content_selection_type === "custom"
                        }
                        onChange={() => handleContentSelectionChange("custom")}
                        className="focus:ring-brand h-4 w-4 text-brand border-gray-300"
                      />
                      <label
                        htmlFor="custom_content"
                        className="ml-3 block text-sm text-gray-700"
                      >
                        Create Custom Content
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Other options - Right column  customizeable game configs should go here*/}
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-700">
                  Other Options
                </h5>
                <div className="mt-2">
                  <GameConfigSetter
                    gameType={getSelectedGameName() || undefined}
                    gameConfig={localTouchpoint.game_config}
                    onChange={(config) => {
                      // Update local state
                      setLocalTouchpoint((prev) => ({
                        ...prev,
                        game_config: config,
                      }));

                      // Update parent state immediately
                      onChange({
                        ...localTouchpoint,
                        game_config: config,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Custom content editor - Only when custom content is selected and supported */}
        {localTouchpoint.content_selection_type === "custom" &&
          isCustomContentSupported() && (
            <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900">
                Custom Content Editor
              </h4>
              <p className="text-sm text-gray-500">
                Create custom content for this touchpoint. Content will be
                scoped to this journey.
                {localTouchpoint.custom_content?.length
                  ? ` (${localTouchpoint.custom_content.length} items created)`
                  : ""}
              </p>

              {/* Render the appropriate content editor based on game type */}
              {getSelectedGameName() === THIS_OR_THAT && (
                <JourneyThisOrThatCreateForm
                  contentRows={contentRows}
                  setContentRows={handleContentRowsUpdate}
                  handleSubmit={handleCustomContentSubmit}
                  isLoading={false}
                  touchpointId={touchpointIndex.toString()}
                  key={`this-or-that-form-${touchpointIndex}`}
                />
              )}

              {getSelectedGameName() === TRIVIA && (
                <JourneyTriviaCreateForm
                  contentRows={contentRows}
                  setContentRows={handleContentRowsUpdate}
                  isLoading={false}
                  touchpointId={touchpointIndex.toString()}
                  key={`trivia-form-${touchpointIndex}`}
                />
              )}

              {getSelectedGameName() === PICTURE_PUZZLE && (
                <JourneyPicturePuzzleCreateForm
                  contentRows={contentRows}
                  setContentRows={handleContentRowsUpdate}
                  isLoading={false}
                  teamId={teamId}
                  touchpointId={touchpointIndex.toString()}
                  key={`picture-puzzle-form-${touchpointIndex}`}
                />
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default TouchpointConfigStep;
